import { CSRF_HEADER } from "@lms/backend/lib/csrf-constants";
import { apiUrl } from "@/services/http/api-url";
import { fetchCsrfToken } from "@/services/http/csrf-token";
import { usePlatformAuthStore, type PlatformUser } from "@/stores/platformAuthStore";

/**
 * MT-1 AUTH-009 (FR-6.6): the platform session's refresh, apart from the school's
 * (`session-refresh.ts`). ONE in-flight `POST /platform/auth/refresh` (CSRF required: the
 * `/api/platform` prefix is not exempt; the credential is the `lms_platform_refresh` cookie).
 * A refusal clears the platform store only.
 */
export type PlatformRefreshOutcome = "refreshed" | "rejected" | "unavailable";

let inFlight: Promise<PlatformRefreshOutcome> | null = null;

export function resetPlatformSessionRefreshForTests(): void {
  inFlight = null;
}

async function performRefresh(): Promise<PlatformRefreshOutcome> {
  let res: Response;
  try {
    res = await fetch(apiUrl("/platform/auth/refresh"), {
      method: "POST",
      credentials: "include",
      headers: { [CSRF_HEADER]: await fetchCsrfToken() },
    });
  } catch {
    return "unavailable";
  }
  if (res.status === 401 || res.status === 403) {
    usePlatformAuthStore.getState().clearSession();
    return "rejected";
  }
  if (!res.ok) return "unavailable";
  try {
    const body = (await res.json()) as { data?: { accessToken?: string; user?: PlatformUser } };
    const accessToken = body.data?.accessToken;
    const user = body.data?.user ?? usePlatformAuthStore.getState().user;
    if (!accessToken || !user) return "unavailable";
    usePlatformAuthStore.getState().renewSession(accessToken, user);
    return "refreshed";
  } catch {
    return "unavailable";
  }
}

/** Single-flight platform refresh. */
export function refreshPlatformSessionOnce(): Promise<PlatformRefreshOutcome> {
  if (inFlight) return inFlight;
  inFlight = performRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Ends the platform session on the server (best effort) and clears the platform store. */
export async function endPlatformSession(): Promise<void> {
  const token = usePlatformAuthStore.getState().accessToken;
  try {
    await fetch(apiUrl("/platform/auth/logout"), {
      method: "POST",
      credentials: "include",
      headers: {
        [CSRF_HEADER]: await fetchCsrfToken(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  } catch {
    // The server session still ends at its own idle limit.
  }
  usePlatformAuthStore.getState().clearSession();
}
